import { api } from "@/lib/apiClient";
import type { UploadedFile } from "@/lib/catalog/types";
import { mapProductImagesFromApi, serializeProductImagesForApi } from "@/lib/catalog/productsStore";

export type ProductAdListingPlatform = "olx" | "pak-wheels" | "wise-wheels";

export type ProductAdListingRow = {
  id: number;
  productId: string;
  platform: ProductAdListingPlatform;
  salePrice: number | null;
  images: UploadedFile[];
  platformLink: string | null;
  createdAt: string;
};

export type CreateProductAdListingInput = {
  productId: string;
  platform: ProductAdListingPlatform;
  salePrice: number | null;
  platformLink: string | null;
  images: UploadedFile[];
};

export type UpdateProductAdListingInput = {
  salePrice: number | null;
  images: UploadedFile[];
};

type ApiListingRow = {
  id: number;
  product_id: number;
  platform: ProductAdListingPlatform;
  sale_price: number | string | null;
  images?: unknown;
  platform_link: string | null;
  created_at: string;
};

function mapListingRow(row: ApiListingRow): ProductAdListingRow {
  const sp = row.sale_price;
  const salePrice =
    sp == null || sp === ""
      ? null
      : typeof sp === "number"
        ? sp
        : Number.parseFloat(String(sp));
  return {
    id: row.id,
    productId: String(row.product_id),
    platform: row.platform,
    salePrice: Number.isFinite(salePrice) ? salePrice : null,
    images: mapProductImagesFromApi(row.images),
    platformLink: row.platform_link,
    createdAt: row.created_at,
  };
}

type ListResponse = { data?: ApiListingRow[] };

export async function fetchProductAdListings(): Promise<ProductAdListingRow[]> {
  const response = await api.get<ListResponse>("/api/v1/catalog/product-ad-listings");
  const rows = response.data?.data;
  if (!Array.isArray(rows)) return [];
  return rows.map(mapListingRow);
}

type CreateProductAdListingResponse = { message?: string; data?: unknown };

export async function createProductAdListing(input: CreateProductAdListingInput): Promise<void> {
  await api.post<CreateProductAdListingResponse>("/api/v1/catalog/product-ad-listings", {
    product_id: input.productId,
    platform: input.platform,
    sale_price: input.salePrice,
    platform_link: input.platformLink,
    images: serializeProductImagesForApi(input.images),
  });
}

export async function updateProductAdListing(id: number, input: UpdateProductAdListingInput): Promise<void> {
  await api.patch(`/api/v1/catalog/product-ad-listings/${id}`, {
    sale_price: input.salePrice,
    images: serializeProductImagesForApi(input.images),
  });
}
